import type { ComponentProps } from 'react';
import { Eye, ShieldCheck } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  capabilityTooltip,
  type InvoicingCapability,
} from './useInvoicingCapability';

type GatedButtonProps = ComponentProps<typeof Button> & {
  capability: InvoicingCapability;
};

/**
 * A Button that renders normally for the invoicing owner and disabled at 45%
 * opacity with an explanatory tooltip for view-only finance users.
 */
export function GatedButton({
  capability,
  className,
  disabled,
  ...props
}: GatedButtonProps) {
  if (capability.canAct) {
    return <Button className={className} disabled={disabled} {...props} />;
  }
  const message = capabilityTooltip(capability);
  return (
    <Tooltip delayDuration={150}>
      {/* Disabled buttons swallow pointer events; the span keeps the tooltip
          triggerable. */}
      <TooltipTrigger asChild>
        <span className="tw:inline-flex" title={message}>
          <Button
            className={cn('tw:opacity-45', className)}
            disabled
            {...props}
          />
        </span>
      </TooltipTrigger>
      <TooltipContent className="tw:max-w-64">{message}</TooltipContent>
    </Tooltip>
  );
}

/** Header chip showing the viewer's capability. */
export function CapabilityChip({
  capability,
}: {
  capability: InvoicingCapability;
}) {
  if (capability.loading) return null;
  return (
    <span className="tw:inline-flex tw:h-7 tw:items-center tw:gap-1.5 tw:rounded-full tw:border tw:border-border tw:px-2.5 tw:text-xs tw:font-medium tw:text-muted-foreground">
      {capability.canAct ? (
        <>
          <ShieldCheck className="tw:size-3.5" />
          Invoicing owner
        </>
      ) : (
        <>
          <Eye className="tw:size-3.5" />
          Finance · view only
        </>
      )}
    </span>
  );
}
